import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';
import { BASE_URL, DEFAULT_AVATAR } from '../config';
import SecureImage from './SecureImage';
import BrandIcon from './BrandIcon';
import ThemeSwitcher from './ThemeSwitcher';
import { Menu, X, LogOut } from 'lucide-react';

const NAV_ITEMS = [
  { to: '/dashboard', label: '我的八字' },
  { to: '/bazi/new', label: '新建排盘' },
  { to: '/community', label: '社区' },
  { to: '/almanac', label: '万年历' }
];

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // 路由变化时关闭移动端抽屉
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // 抽屉打开时锁定背景滚动
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const handleLogout = () => {
    setDrawerOpen(false);
    logout();
    navigate('/auth');
  };

  const avatarSrc = user?.avatarUrl ? `${BASE_URL}${user.avatarUrl}` : DEFAULT_AVATAR;
  const navLinkClass = ({ isActive }) => (isActive ? 'navbar-link active' : 'navbar-link');

  return (
    <nav className="navbar">
      <div className="navbar-content">
        <div className="navbar-head">
          <Link to="/dashboard" className="navbar-brand">
            <BrandIcon size={26} className="brand-icon" />
            <span>八字命理排盘系统</span>
          </Link>
          <button
            type="button"
            className="navbar-hamburger"
            aria-label="打开菜单"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu size={24} />
          </button>
        </div>
        <div className="navbar-menu">
          {/* 菜单项使用 NavLink 以支持当前页面高亮 */}
          {NAV_ITEMS.map(item => (
            <NavLink key={item.to} to={item.to} className={navLinkClass}>{item.label}</NavLink>
          ))}
          <ThemeSwitcher />
          <div className="navbar-user" ref={menuRef}>
            <SecureImage className="navbar-avatar" src={avatarSrc} alt="avatar" onClick={() => setShowMenu(v => !v)} />
            <span className="user-name" onClick={() => setShowMenu(v => !v)}>{user?.nickname || user?.username}</span>
            {showMenu && (
              <div className="dropdown-menu">
                <Link to="/profile" className="dropdown-item" onClick={() => setShowMenu(false)}>个人资料</Link>
                {user?.admin === 1 && (
                  <>
                    <Link to="/admin/users" className="dropdown-item" onClick={() => setShowMenu(false)}>成员管理</Link>
                    <Link to="/admin/labels" className="dropdown-item" onClick={() => setShowMenu(false)}>八字标签管理</Link>
                    <Link to="/admin/groups" className="dropdown-item" onClick={() => setShowMenu(false)}>成员分组管理</Link>
                  </>
                )}
                <button className="dropdown-item" style={{ color: 'var(--danger)' }} onClick={handleLogout}>退出登录</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 移动端右侧滑出抽屉 */}
      <div
        className={`navbar-drawer-overlay ${drawerOpen ? 'open' : ''}`}
        onClick={() => setDrawerOpen(false)}
      />
      <aside className={`navbar-drawer ${drawerOpen ? 'open' : ''}`} aria-hidden={!drawerOpen}>
        <div className="drawer-head">
          <div className="drawer-user">
            <SecureImage className="drawer-avatar" src={avatarSrc} alt="avatar" />
            <span className="drawer-user-name">{user?.nickname || user?.username}</span>
          </div>
          <button type="button" className="drawer-close" aria-label="关闭菜单" onClick={() => setDrawerOpen(false)}>
            <X size={22} />
          </button>
        </div>
        <nav className="drawer-nav">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? 'drawer-link active' : 'drawer-link')}
              onClick={() => setDrawerOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
          <div className="drawer-divider" />
          <NavLink
            to="/profile"
            className={({ isActive }) => (isActive ? 'drawer-link active' : 'drawer-link')}
            onClick={() => setDrawerOpen(false)}
          >
            个人资料
          </NavLink>
          {user?.admin === 1 && (
            <>
              <NavLink to="/admin/users" className={({ isActive }) => (isActive ? 'drawer-link active' : 'drawer-link')} onClick={() => setDrawerOpen(false)}>成员管理</NavLink>
              <NavLink to="/admin/labels" className={({ isActive }) => (isActive ? 'drawer-link active' : 'drawer-link')} onClick={() => setDrawerOpen(false)}>八字标签管理</NavLink>
              <NavLink to="/admin/groups" className={({ isActive }) => (isActive ? 'drawer-link active' : 'drawer-link')} onClick={() => setDrawerOpen(false)}>成员分组管理</NavLink>
            </>
          )}
          <div className="drawer-divider" />
          <div className="drawer-section-title">界面主题</div>
          <ThemeSwitcher variant="inline" />
        </nav>
        <button type="button" className="drawer-logout" onClick={handleLogout}>
          <LogOut size={16} /> 退出登录
        </button>
      </aside>
    </nav>
  );
};

export default Navbar;
